/*
 * Copyright © 2024-2026 symecode.com. All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution is prohibited.
 */


import React, { useState, useEffect, useRef } from 'react';
import BackButton from '../common/BackButton';
import { useNavigate } from 'react-router-dom';
import { Editor } from '@tinymce/tinymce-react';
import { apiClient } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { ParticipantType, Company, ComponentType, Component } from '../../types';
import PdfSearchModal from '../bids/PdfSearchModal';
import './CreatePdf.css';
import './ComponentCollectionManager.css';

// Import TinyMCE
import 'tinymce/tinymce';
import 'tinymce/icons/default';
import 'tinymce/themes/silver';
import 'tinymce/models/dom';
import 'tinymce/skins/ui/oxide/skin.css';

// Import plugins
import 'tinymce/plugins/advlist';
import 'tinymce/plugins/autolink';
import 'tinymce/plugins/lists';
import 'tinymce/plugins/link';
import 'tinymce/plugins/image';
import 'tinymce/plugins/charmap';
import 'tinymce/plugins/preview';
import 'tinymce/plugins/anchor';
import 'tinymce/plugins/searchreplace';
import 'tinymce/plugins/visualblocks';
import 'tinymce/plugins/code';
import 'tinymce/plugins/fullscreen';
import 'tinymce/plugins/insertdatetime';
import 'tinymce/plugins/media';
import 'tinymce/plugins/table';
import 'tinymce/plugins/help';
import 'tinymce/plugins/wordcount';

import tinymce from 'tinymce/tinymce';
import { registerMustachePlugin } from '../tinymce/mustacheTemplatePlugin';
registerMustachePlugin(tinymce);

const CreatePdf: React.FC = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [componentType, setComponentType] = useState<ComponentType>(ComponentType.ITEM);
  const frontPageTemplateEditorRef = useRef<any>(null);

  // Calculate default expiration date (5 years from now)
  const getDefaultExpirationDate = () => {
    const date = new Date();
    date.setFullYear(date.getFullYear() + 5);
    return date.toISOString().split('T')[0]; // Format as YYYY-MM-DD
  };

  const [formData, setFormData] = useState({
    name: '',
    manufacturerName: '',
    manufacturerCode: '',
    price: '',
    expirationDate: getDefaultExpirationDate(),
    companyId: '',
  });
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Collection items state (for COLLECTION type)
  interface PendingCollectionItem {
    component: Component;
    suggestedCount: number;
  }
  const [pendingItems, setPendingItems] = useState<PendingCollectionItem[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [editingCount, setEditingCount] = useState<number>(1);

  useEffect(() => {
    if (currentUser?.type === ParticipantType.ADMIN) {
      fetchCompanies();
    } else if (currentUser?.company) {
      // For managers, auto-set their company
      setFormData(prev => ({
        ...prev,
        companyId: currentUser.company!.id.toString()
      }));
    }
  }, [currentUser]);

  const fetchCompanies = async () => {
    try {
      const response = await apiClient.getAllCompanies();
      if (response.success && response.companies) {
        setCompanies(response.companies);
      }
    } catch (err) {
      console.error('Error fetching companies:', err);
    }
  };

  // Collection item handlers
  const handleAddComponent = (component: Component) => {
    setPendingItems(prev => [...prev, { component, suggestedCount: 1 }]);
    setShowAddModal(false);
  };

  const handleRemoveItem = (index: number) => {
    setPendingItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleStartEditCount = (index: number, currentCount: number) => {
    setEditingItemIndex(index);
    setEditingCount(currentCount);
  };

  const handleSaveCount = (index: number) => {
    setPendingItems(prev => prev.map((item, i) =>
      i === index ? { ...item, suggestedCount: editingCount } : item
    ));
    setEditingItemIndex(null);
  };

  const handleCancelEditCount = () => {
    setEditingItemIndex(null);
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    setPendingItems(prev => {
      const newItems = [...prev];
      [newItems[index - 1], newItems[index]] = [newItems[index], newItems[index - 1]];
      return newItems;
    });
  };

  const handleMoveDown = (index: number) => {
    if (index === pendingItems.length - 1) return;
    setPendingItems(prev => {
      const newItems = [...prev];
      [newItems[index], newItems[index + 1]] = [newItems[index + 1], newItems[index]];
      return newItems;
    });
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Validate file type
      if (file.type !== 'application/pdf') {
        setError('Only component files (PDF format) are allowed');
        setSelectedFile(null);
        return;
      }

      setSelectedFile(file);
      setError(null);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }

    // Only validate manufacturer fields for ITEM type
    if (componentType === ComponentType.ITEM) {
      if (!formData.manufacturerName.trim()) {
        setError('Manufacturer name is required for Item type');
        return;
      }

      if (!formData.manufacturerCode.trim()) {
        setError('Manufacturer code is required for Item type');
        return;
      }
    }

    if (!formData.companyId) {
      setError('Company is required');
      return;
    }

    setIsLoading(true);

    try {
      // Get content from TinyMCE editor
      let frontPageTemplateContent = '';
      if (frontPageTemplateEditorRef.current) {
        // Wait for any pending uploads
        if (typeof frontPageTemplateEditorRef.current.uploadImages === 'function') {
          await frontPageTemplateEditorRef.current.uploadImages();
        }
        await new Promise(resolve => setTimeout(resolve, 500));
        frontPageTemplateContent = frontPageTemplateEditorRef.current.getContent();
      }

      // Validate: for ITEM type, must have at least one of file OR template (can have both)
      // For COLLECTION type, file and template are optional
      const hasFile = selectedFile !== null;
      const hasTemplate = frontPageTemplateContent && frontPageTemplateContent.trim() !== '';

      if (componentType === ComponentType.ITEM && !hasFile && !hasTemplate) {
        setError('At least one of component file or front page template is required for Item type');
        setIsLoading(false);
        return;
      }

      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name.trim());
      if (selectedFile) {
        formDataToSend.append('file', selectedFile);
      }

      // Only send manufacturer fields if they have values (for ITEM types)
      if (formData.manufacturerName && formData.manufacturerName.trim()) {
        formDataToSend.append('manufacturerName', formData.manufacturerName.trim());
      }
      if (formData.manufacturerCode && formData.manufacturerCode.trim()) {
        formDataToSend.append('manufacturerCode', formData.manufacturerCode.trim());
      }

      if (formData.price) {
        formDataToSend.append('price', formData.price);
      }

      if (formData.expirationDate) {
        formDataToSend.append('expirationDate', formData.expirationDate);
      }

      if (formData.companyId) {
        formDataToSend.append('companyId', formData.companyId);
      }

      if (frontPageTemplateContent) {
        formDataToSend.append('frontPageTemplate', frontPageTemplateContent);
      }

      // Add component type
      formDataToSend.append('type', componentType);

      const response = await apiClient.uploadComponentWithDetails(formDataToSend);

      if (response.success) {
        // If it's a COLLECTION with pending items, add them
        if (componentType === ComponentType.COLLECTION && response.component?.id && pendingItems.length > 0) {
          const collectionId = response.component.id;

          // Add each pending item to the collection
          for (const item of pendingItems) {
            try {
              await apiClient.addItemToCollection(collectionId, item.component.id, item.suggestedCount);
            } catch (addErr) {
              console.error('Error adding item to collection:', addErr);
              // Continue with other items even if one fails
            }
          }

          navigate(`/components/${collectionId}`);
        } else if (componentType === ComponentType.COLLECTION && response.component?.id) {
          navigate(`/components/${response.component.id}`);
        } else {
          navigate('/components');
        }
      } else {
        setError(response.error || 'Failed to upload PDF');
      }
    } catch (err: any) {
      console.error('Error uploading PDF:', err);
      setError(err.response?.data?.error || err.message || 'An error occurred while uploading the PDF');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/components');
  };

  return (
    <div className="create-pdf-container">
      <BackButton to="/components" />
      <div className="create-pdf-header">
        <h2>Upload New Component</h2>
      </div>

      <form onSubmit={handleSubmit} className="create-pdf-form">
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="componentType">
            Component Type <span className="required">*</span>
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.5rem' }}>
            <span style={{ fontWeight: componentType === ComponentType.ITEM ? '600' : '400', color: componentType === ComponentType.ITEM ? '#059669' : '#6b7280' }}>
              Item
            </span>
            <div className="toggle-switch-container">
              <input
                type="checkbox"
                id="componentTypeToggle"
                checked={componentType === ComponentType.COLLECTION}
                onChange={() => setComponentType(
                  componentType === ComponentType.ITEM
                    ? ComponentType.COLLECTION
                    : ComponentType.ITEM
                )}
                disabled={isLoading}
                className="toggle-checkbox"
              />
              <label htmlFor="componentTypeToggle" className="toggle-label">
                <span className="toggle-inner" />
                <span className="toggle-switch" />
              </label>
            </div>
            <span style={{ fontWeight: componentType === ComponentType.COLLECTION ? '600' : '400', color: componentType === ComponentType.COLLECTION ? '#059669' : '#6b7280' }}>
              Collection
            </span>
          </div>
          <small style={{ display: 'block', marginTop: '0.5rem', color: '#6b7280' }}>
            {componentType === ComponentType.ITEM
              ? 'A single component with its own PDF file and/or template'
              : 'A collection that groups multiple components together'}
          </small>
        </div>

        {componentType === ComponentType.ITEM && (
          <div className="form-group">
            <label htmlFor="file">
              Component File
            </label>
            <input
              type="file"
              id="file"
              name="file"
              accept=".pdf,application/pdf"
              onChange={handleFileChange}
              disabled={isLoading}
            />
            {selectedFile && (
              <div className="file-info">
                Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="name">
            Name <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            disabled={isLoading}
            required
            placeholder="Enter component name"
          />
        </div>

        {componentType === ComponentType.ITEM && (
          <>
            <div className="form-group">
              <label htmlFor="manufacturerName">
                Manufacturer Name
              </label>
              <input
                type="text"
                id="manufacturerName"
                name="manufacturerName"
                value={formData.manufacturerName}
                onChange={handleChange}
                disabled={isLoading}
                placeholder="Enter manufacturer name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="manufacturerCode">
                Manufacturer Code
              </label>
              <input
                type="text"
                id="manufacturerCode"
                name="manufacturerCode"
                value={formData.manufacturerCode}
                onChange={handleChange}
                disabled={isLoading}
                placeholder="Enter manufacturer code"
              />
            </div>

            <div className="form-group">
              <label htmlFor="price">
                Price
              </label>
              <input
                type="number"
                id="price"
                name="price"
                value={formData.price}
                onChange={handleChange}
                disabled={isLoading}
                placeholder="Enter price"
                step="0.01"
                min="0"
              />
            </div>
          </>
        )}

        <div className="form-group">
          <label htmlFor="expirationDate">
            Expiration Date
          </label>
          <input
            type="date"
            id="expirationDate"
            name="expirationDate"
            value={formData.expirationDate}
            onChange={handleChange}
            disabled={isLoading}
          />
        </div>

        {currentUser?.type === ParticipantType.ADMIN ? (
          <div className="form-group">
            <label htmlFor="companyId">
              Company <span className="required">*</span>
            </label>
            <select
              id="companyId"
              name="companyId"
              value={formData.companyId}
              onChange={handleChange}
              disabled={isLoading}
              required
            >
              <option value="">Select a company</option>
              {companies.map(company => (
                <option key={company.id} value={company.id}>
                  {company.name}
                </option>
              ))}
            </select>
          </div>
        ) : currentUser?.company ? (
          <div className="form-group">
            <label htmlFor="companyDisplay">
              Company <span className="required">*</span>
            </label>
            <input
              type="text"
              id="companyDisplay"
              value={currentUser.company.name}
              disabled
              style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
            />
          </div>
        ) : null}

        {componentType === ComponentType.COLLECTION && (
          <div className="collection-manager">
            <div className="collection-header">
              <h3>Collection Items ({pendingItems.length})</h3>
              <button
                type="button"
                onClick={() => setShowAddModal(true)}
                className="btn btn-primary btn-small"
                disabled={isLoading}
              >
                + Add Component
              </button>
            </div>

            {pendingItems.length === 0 ? (
              <div className="empty-state">
                <p>No components in this collection yet.</p>
                <p>Click "Add Component" to add components to this collection.</p>
              </div>
            ) : (
              <div className="collection-items-list">
                {pendingItems.map((item, index) => (
                  <div key={`${item.component.id}-${index}`} className="collection-item-card">
                    <div className="item-order-badge">#{index + 1}</div>

                    <div className="item-content">
                      <div className="item-main-info">
                        <h4>{item.component.name}</h4>
                        {item.component.manufacturerName && (
                          <div className="item-meta">
                            {item.component.manufacturerName}
                            {item.component.manufacturerCode && ` | ${item.component.manufacturerCode}`}
                            {item.component.price && ` | $${item.component.price.toFixed(2)}`}
                          </div>
                        )}
                      </div>

                      <div className="item-count-section">
                        <label>Suggested Count:</label>
                        {editingItemIndex === index ? (
                          <div className="count-edit-controls">
                            <input
                              type="number"
                              min="1"
                              value={editingCount}
                              onChange={(e) => setEditingCount(parseInt(e.target.value) || 1)}
                              className="count-input"
                            />
                            <button
                              type="button"
                              onClick={() => handleSaveCount(index)}
                              className="btn btn-success btn-small"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={handleCancelEditCount}
                              className="btn btn-secondary btn-small"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="count-display">
                            <span className="count-value">{item.suggestedCount}</span>
                            <button
                              type="button"
                              onClick={() => handleStartEditCount(index, item.suggestedCount)}
                              className="btn btn-link btn-small"
                            >
                              Edit
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="item-actions">
                      <button
                        type="button"
                        onClick={() => handleMoveUp(index)}
                        disabled={index === 0}
                        className="btn btn-icon"
                        title="Move up"
                      >
                        ▲
                      </button>
                      <button
                        type="button"
                        onClick={() => handleMoveDown(index)}
                        disabled={index === pendingItems.length - 1}
                        className="btn btn-icon"
                        title="Move down"
                      >
                        ▼
                      </button>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="btn btn-danger btn-small"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="form-group">
          <label htmlFor="frontPageTemplate">Front Page Template</label>
          <Editor
            onInit={(evt, editor) => frontPageTemplateEditorRef.current = editor}
            licenseKey="gpl"
            init={{
              base_url: '/tinymce',
              suffix: '.min',
              height: 400,
              menubar: true,
              promotion: false,
              plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                'insertdatetime', 'media', 'table', 'help', 'wordcount', 'mustachetemplate'
              ],
              toolbar: 'undo redo | blocks | inserttemplate | ' +
                'bold italic forecolor | alignleft aligncenter ' +
                'alignright alignjustify | bullist numlist outdent indent | ' +
                'image | removeformat | help',
              mustacheTemplateConfig: { collectionId: null, mode: 'author' },
              content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
              images_upload_handler: (blobInfo: any, progress: any) => new Promise<string>((resolve, reject) => {
                const xhr = new XMLHttpRequest();
                xhr.withCredentials = false;

                xhr.upload.onprogress = (e) => {
                  progress(e.loaded / e.total * 100);
                };

                xhr.onload = () => {
                  if (xhr.status === 403) {
                    reject({ message: 'HTTP Error: ' + xhr.status, remove: true });
                    return;
                  }

                  if (xhr.status < 200 || xhr.status >= 300) {
                    reject('HTTP Error: ' + xhr.status);
                    return;
                  }

                  const json = JSON.parse(xhr.responseText);
                  if (!json || typeof json.location != 'string') {
                    reject('Invalid JSON: ' + xhr.responseText);
                    return;
                  }

                  resolve(json.location);
                };

                xhr.onerror = () => {
                  reject('Image upload failed due to a XHR Transport error. Code: ' + xhr.status);
                };

                const formData = new FormData();
                formData.append('file', blobInfo.blob(), blobInfo.filename());

                xhr.open('POST', '/api/tinymce/upload-image');
                const token = localStorage.getItem('jwt_token');
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
                xhr.send(formData);
              }),
              automatic_uploads: true,
              paste_data_images: true,
              images_reuse_filename: false,
              file_picker_types: 'image',
              images_file_types: 'jpg,jpeg,png,gif,webp',
              relative_urls: true,
              remove_script_host: true,
              convert_urls: false
            }}
            disabled={isLoading}
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            disabled={isLoading}
            className="cancel-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="submit-button"
          >
            {isLoading ? 'Uploading...' : 'Upload Component'}
          </button>
        </div>
      </form>

      {showAddModal && (
        <PdfSearchModal
          onClose={() => setShowAddModal(false)}
          onSelectComponent={handleAddComponent}
          excludedComponentIds={pendingItems.map(item => item.component.id)}
        />
      )}
    </div>
  );
};

export default CreatePdf;
